import type {
  FilterReturn,
  BodyFilters,
  PostBodyFilters,
  FiltersChangeArguments,
  queryType,
  RangeFilteringValue,
  ActiveFilter,
  filtersValues,
} from '~/interfaces/app';
import type {
  BrandFilter,
  ShopFilter,
  EnumFilter,
  RangeFilter,
  ResponseCategoryFilters,
} from '~/interfaces/api';

export function useFilters(
  filtersParams: ResponseCategoryFilters,
  tryGetCount: () => void,
  getItems: (loadFirst: boolean, newFilters: boolean) => Promise<any>,
  useActiveList = false,
  showPresetsFilters = false
) {
  let filtersP = filtersParams;
  const router = useRouter();
  const route = useRoute();
  const values = ref<filtersValues>({
    filters: {},
    price: null,
    brands: null,
    shops: null,
    discount: false,
    best_price: false,
    initiated: false,
    active: useActiveList ? [] : null,
    reqBody: { filters: [] },
  });

  const resetValues = () => {
    values.value = {
      filters: {},
      price: null,
      brands: null,
      shops: null,
      discount: false,
      best_price: false,
      initiated: false,
      active: useActiveList ? [] : null,
      reqBody: { filters: [] },
    };
  };

  const isChanged = computed(() => {
    const isFilters =
      values?.value?.filters &&
      typeof values.value.filters === 'object' &&
      Object.keys instanceof Function;
    const filters = isFilters
      ? Object.keys(values.value.filters).some((key: string) => {
          const query =
            route.query[`attr-${key}`] ||
            // filter with ONLY one boolean value as route.value.query[`attr-${key}`] is null or undefined, null is switch on filter, undefined switch off, so we need track this change when clearing all filters
            route.query[`attr-${key}`] === null ||
            route.query[`range-${key}`];
          return values.value.filters[key].query !== query;
        })
      : false;
    const isPriceChanged = values.value.price?.query !== route.query.price;
    const isBrandChanged = values.value.brands?.query !== route.query.brand_id;
    const isShopsChanged = values.value.shops?.query !== route.query.shop_ids;
    const isDiscountChanged = values.value.discount !== !!route.query.discount;
    const isBestPriceChanged =
      values.value.best_price !== !!route.query.best_price;
    const hasFilters = Boolean(filters);

    return (
      isPriceChanged ||
      isBrandChanged ||
      isDiscountChanged ||
      isBestPriceChanged ||
      hasFilters ||
      isShopsChanged
    );
  });

  const atLeastOne = computed(() => {
    const isFilters =
      values?.value?.filters &&
      typeof values.value.filters === 'object' &&
      Object.values instanceof Function;
    const filters = isFilters
      ? Object.values(values.value.filters).some(
          (el) => el?.query !== undefined
        )
      : false;

    return (
      values.value.price?.query !== undefined ||
      values.value.discount ||
      values.value.brands?.query !== undefined ||
      filters
    );
  });

  function initiateFilters(
    clear = false,
    inputFilters: ResponseCategoryFilters | undefined = undefined
  ): void {
    if (inputFilters) {
      filtersP = inputFilters;
    }

    if (filtersP) {
      if (filtersP?.filters) {
        values.value.filters = filtersP.filters.reduce((sum, el) => {
          const prefix = el.type === 'RANGE' ? 'range' : 'attr';
          let query: queryType =
            !clear &&
            route.query[`${prefix}-${el.id}`] &&
            typeof route.query[`${prefix}-${el.id}`] === 'string'
              ? <string>route.query[`${prefix}-${el.id}`]
              : undefined;

          let val: FilterReturn = query
            ? query.split(',').map((el) => +el)
            : [];
          if (showPresetsFilters && el.selected && !clear) {
            switch (el.type) {
              case 'ENUM': {
                const allowedIds =
                  el.allowed_values?.map((item) => String(item.id)) || [];
                const selectedIds = (<EnumFilter>el).selected.filter((item) =>
                  allowedIds.includes(String(item))
                );
                query = selectedIds.join(',');
                val = selectedIds;
                break;
              }
              case 'RANGE': {
                val = {
                  min: (<RangeFilter>el).selected?.min
                    ? (<RangeFilter>el).selected.min
                    : null,
                  max: (<RangeFilter>el).selected?.max
                    ? (<RangeFilter>el).selected.max
                    : null,
                };
                query = `${val.min},${val.max}`;
                break;
              }
              case 'BOOL': {
                query = null;
                break;
              }
            }
          } else if (!showPresetsFilters || !query) {
            switch (el.type) {
              case 'RANGE': {
                val =
                  query && !Number.isNaN(val[1]) && !Number.isNaN(val[0])
                    ? {
                        min: +val[0],
                        max: Number(val[1]),
                      }
                    : {
                        min: +(<RangeFilter>el).min_value,
                        max: Number((<RangeFilter>el).max_value),
                      };
                break;
              }

              case 'BOOL': {
                val = clear
                  ? false
                  : route.query[`attr-${el.id}`] === 'true' ||
                    route.query[`attr-${el.id}`] === null;
                query = val ? null : undefined;
                break;
              }
            }
          }

          return {
            ...sum,
            [el.id]: {
              value: val,
              query,
            },
          };
        }, {});
      }

      values.value.discount = clear ? false : !!route.query.discount;
      values.value.best_price = clear ? false : !!route.query.best_price;

      if (filtersP.price) {
        const query =
          !clear && route.query.price && typeof route.query.price === 'string'
            ? route.query.price
            : undefined;

        const val = query && query.split(',').map((el) => +el);

        values.value.price = {
          value: val
            ? {
                min: val[0],
                max: val[1],
              }
            : {
                min: filtersP.price.min_value,
                max: filtersP.price.max_value,
              },
          query,
        };
      }

      if (filtersP.brands) {
        const selectedBrands = filtersP.brands.filter(
          (i: BrandFilter) => !!i.selected
        );
        if (showPresetsFilters && selectedBrands.length && !clear) {
          const query = selectedBrands.map((el) => el?.id);
          values.value.brands = {
            value: query,
            query: query.join(','),
          };
        } else {
          const query =
            !clear &&
            route.query.brand_id &&
            typeof route.query.brand_id === 'string'
              ? route.query.brand_id
              : undefined;

          values.value.brands = {
            value: query ? query.split(',').map((el) => +el) : [],
            query,
          };
        }
      }
      if (filtersP.shops) {
        const selectedShops = filtersP.shops.filter(
          (i: ShopFilter) => !!i.selected
        );
        if (showPresetsFilters && selectedShops.length && !clear) {
          const query = selectedShops.map((el) => el?.id);
          values.value.shops = {
            value: query,
            query: query.join(','),
          };
        } else {
          const query =
            !clear &&
            route.query.shop_ids &&
            typeof route.query.shop_ids === 'string'
              ? route.query.shop_ids
              : undefined;

          values.value.shops = {
            value: query ? query.split(',').map((el) => +el) : [],
            query,
          };
        }
      }

      if (showPresetsFilters) changeFiltersQuery();
      if (!clear) {
        getListOfActive();
      }
      values.value.initiated = true;
    }
  }

  function formatFiltersForRequest() {
    const filters: BodyFilters[] = [];
    const isFilters =
      values?.value?.filters &&
      typeof values.value.filters === 'object' &&
      Object.keys instanceof Function;
    if (isFilters) {
      Object.keys(values.value.filters).forEach((key: string) => {
        if (values.value.filters[key].query !== undefined)
          filters.push({
            id: +key,
            value: values.value.filters[key].value,
          });
      });
    }
    const newFilters: PostBodyFilters = { filters };

    if (values.value.brands?.query !== undefined)
      newFilters.brand_id = values.value.brands.value;
    if (values.value.shops?.query !== undefined)
      newFilters.shop_ids = values.value.shops.value;

    if (values.value.price?.query !== undefined)
      newFilters.price = values.value.price.value;

    if (values.value.discount) newFilters.discount = 1;
    if (values.value.best_price) newFilters.best_price = 1;

    return newFilters;
  }

  function getFiltersForRequest(withNew = false): PostBodyFilters {
    if (withNew) values.value.reqBody = formatFiltersForRequest();

    return values.value.reqBody;
  }

  function getListOfActive() {
    const filters: ResponseCategoryFilters = <ResponseCategoryFilters>(
      ((filtersP && (<Ref<ResponseCategoryFilters>>filtersP)?.value) ||
        filtersP)
    );
    if (filters && useActiveList) {
      const newActiveList = [];

      if (values.value.price?.query !== undefined)
        newActiveList.push({
          name: `от ${values.value.price.value.min}₽ до ${values.value.price.value.max}₽`,
          filter: 'price',
        });

      if (values.value.discount)
        newActiveList.push({ name: 'Cкидка', filter: 'discount' });

      if (values.value.best_price)
        newActiveList.push({ name: 'Лучшая цена', filter: 'best_price' });

      if (values.value.brands?.query !== undefined)
        values.value.brands.value.forEach((el) => {
          const name = filters?.brands?.find((brand) => brand.id === el)?.name;

          if (name) newActiveList.push({ name, filter: 'brand', id: el });
        });

      if (values.value.shops?.query !== undefined)
        values.value.shops.value.forEach((el) => {
          const name = filters?.shops?.find((v) => v.id === el)?.name;

          if (name) newActiveList.push({ name, filter: 'shop', id: el });
        });

      if (filters.filters && values.value.filters)
        filters.filters.forEach((el) => {
          if (values.value.filters[el.id]?.query !== undefined)
            if (el.type === 'ENUM')
              (<number[]>values.value.filters[el.id].value).forEach((id) => {
                if (!id) return;
                const element = (<EnumFilter>el)?.allowed_values?.find(
                  (val) => val.id === id
                );
                if (element?.name)
                  newActiveList.push({
                    name: element ? element.name : '',
                    filter: 'filter',
                    id: el.id,
                    item_id: id,
                  });
              });
            else if (el.type === 'RANGE') {
              const elemVal = <RangeFilteringValue>(
                values.value?.filters?.[el.id]?.value
              );
              const max = elemVal.max
                ? elemVal.max
                : (<RangeFilter>el)?.max_value;
              const min = elemVal.min
                ? elemVal.min
                : (<RangeFilter>el)?.min_value;
              const name =
                el.name +
                ((<RangeFilter>el).unit ? ', ' + (<RangeFilter>el).unit : '') +
                (elemVal ? `: ${min}-${max}` : '');
              newActiveList.push({
                name,
                filter: 'filter',
                id: el.id,
              });
            } else
              newActiveList.push({
                name: el.name,
                filter: 'filter',
                id: el.id,
              });
        });
      values.value.active = newActiveList;
    }
  }

  function cancelActiveFilter(item: ActiveFilter): void {
    const filters: ResponseCategoryFilters = <ResponseCategoryFilters>(
      ((filtersP && (<Ref<ResponseCategoryFilters>>filtersP)?.value) ||
        filtersP)
    );
    // We leave active (not deleted) filters, i.e. those that are still included
    values.value.active =
      values.value?.active?.filter(
        (el) =>
          el.filter !== item.filter ||
          el.id !== item.id ||
          el.item_id !== item.item_id
      ) || null;
    let queryDelete, curDelete;

    if (item.filter === 'price' && filters?.price) {
      values.value.price = {
        value: {
          min: filters.price.min_value,
          max: filters.price.max_value,
        },
        query: undefined,
      };

      queryDelete = curDelete = { price: undefined };
    }

    if (item.filter === 'discount') {
      values.value.discount = false;

      queryDelete = curDelete = { discount: undefined };
    }
    if (item.filter === 'best_price') {
      values.value.best_price = false;

      queryDelete = curDelete = { best_price: undefined };
    }

    if (item.filter === 'brand' && values.value.brands) {
      const newVal = values.value.brands.value.filter((el) => el !== item.id); // Filter IDs that stay on
      const query = newVal.length > 0 ? newVal.join(',') : undefined; // String: IDs of filters, separated by commas, that stay on
      // route.value.query.brand_id - gets all ID filters from query
      // item.id - ID of the filter to be removed
      // queryUrl - still needed filters left after deletion
      const queryUrl =
        typeof route.query.brand_id === 'string'
          ? route.query.brand_id
              .split(',')
              .filter((el) => +el !== item.id)
              .join(',')
          : undefined;
      values.value.brands = {
        value: newVal,
        query,
      };
      curDelete = { brand_id: newVal.length > 0 ? newVal : undefined };
      queryDelete = { brand_id: queryUrl || undefined };
    }

    if (item.filter === 'shop' && values.value.shops) {
      const newVal = values.value.shops.value.filter((el) => el !== item.id); // Filter IDs that stay on
      const query = newVal.length > 0 ? newVal.join(',') : undefined; // String: IDs of filters, separated by commas, that stay on
      const queryUrl =
        typeof route.query.shop_ids === 'string'
          ? route.query.shop_ids
              .split(',')
              .filter((el) => +el !== item.id)
              .join(',')
          : undefined;
      values.value.shops = {
        value: newVal,
        query,
      };
      curDelete = { shop_ids: newVal.length > 0 ? newVal : undefined };
      queryDelete = { shop_ids: queryUrl || undefined };
    }

    if (item.filter === 'filter' && values.value.filters && item.id) {
      let newQuery;
      let newVal;
      let queryUrl;
      let prefix = 'attr';

      if (item.item_id) {
        newVal = (<number[]>values.value.filters[item.id].value).filter(
          (el) => el !== item.item_id
        );
        newQuery = newVal.length > 0 ? newVal.join(',') : undefined;
        queryUrl =
          typeof route.query[`attr-${item.id}`] === 'string'
            ? (<string>route.query[`attr-${item.id}`])
                .split(',')
                .filter((el) => +el !== item.item_id)
                .join(',')
            : undefined;
      } else if (
        typeof values.value.filters[item.id].value !== 'boolean' &&
        filters?.filters
      ) {
        const rangeSettings = filters.filters.find((el) => el.id === item.id);
        prefix = 'range';
        newVal = {
          min: (<RangeFilter>rangeSettings).min_value,
          max: (<RangeFilter>rangeSettings).max_value,
        };
      }
      values.value.filters[item.id] = {
        value: newVal,
        query: newQuery,
      };

      if (newQuery)
        curDelete = {
          filters: values.value.reqBody.filters.map((el) => {
            return el.id === item.id ? { id: item.id, value: newVal } : el;
          }),
        };
      else
        curDelete = {
          filters: values.value.reqBody.filters.filter(
            (el) => el.id !== item.id
          ),
        };
      queryDelete = { [`${prefix}-${item.id}`]: queryUrl || undefined };
    }
    if (curDelete)
      values.value.reqBody = { ...values.value.reqBody, ...curDelete };
    if (router && queryDelete) {
      router.replace({
        path: route.path,
        query: { ...route.query, ...queryDelete, page: undefined },
      });
      setTimeout(() => {
        getItems(true, false);
      });
    }
    tryGetCount();
  }

  function getWithNewFilters(): Promise<any> {
    if (isChanged.value) {
      changeFiltersQuery(true);
      getListOfActive();
    }

    return getItems(true, true);
  }

  function changeFiltersQuery(delPage = false): void {
    if (isChanged.value && route.query && Object.keys instanceof Function) {
      const otherQuery = Object.keys(route.query).reduce((sum, key) => {
        if (
          /^(range-.+|attr-.+|discount|best_price|shop_ids|brand_id|price)/g.test(
            key
          )
        )
          return sum;
        else
          return {
            ...sum,
            [key]: route.query[key],
          };
      }, {});
      const isFilters =
        values?.value?.filters &&
        typeof values.value.filters === 'object' &&
        Object.keys instanceof Function;
      const newQuery = isFilters
        ? Object.keys(values.value.filters).reduce((sum, key: string) => {
            const query: queryType = values.value.filters[key].query;
            if (query !== undefined) {
              const prefix =
                typeof values.value.filters[key].value === 'object' &&
                typeof (<RangeFilteringValue>values.value.filters[key].value)
                  ?.min === 'number'
                  ? 'range'
                  : 'attr';
              return {
                ...sum,
                [`${prefix}-${key}`]: query || 'true',
              };
            }
            return sum;
          }, {})
        : {};

      if (values.value.brands && values.value.brands.query !== undefined)
        newQuery.brand_id = values.value.brands.query;
      if (values.value.shops && values.value.shops.query !== undefined)
        newQuery.shop_ids = values.value.shops.query;

      if (values.value.price && values.value.price.query !== undefined)
        newQuery.price = values.value.price.query;

      if (values.value.discount) newQuery.discount = '1';
      if (values.value.best_price) newQuery.best_price = '1';
      if (delPage) newQuery.page = undefined;
      if (router) {
        router.replace({
          path: route.path,
          query: { ...otherQuery, ...newQuery },
        });
      }
    }
  }

  function filtersChange(
    { id, val, queryVal }: FiltersChangeArguments,
    changeIn = 'filters',
    preFetch = true
  ): void {
    if (!values?.value) return;
    if (changeIn === 'filters')
      values.value.filters[id] = {
        value: val,
        query: queryVal,
      };

    if (changeIn === 'price')
      values.value.price = {
        value: <RangeFilteringValue>val,
        query: queryVal,
      };

    if (changeIn === 'discount') values.value.discount = <boolean>val;
    if (changeIn === 'best_price') values.value.best_price = <boolean>val;

    if (changeIn === 'brands')
      values.value.brands = {
        value: <number[]>val,
        query: queryVal,
      };
    if (changeIn === 'shops')
      values.value.shops = {
        value: <number[]>val,
        query: queryVal,
      };
    if (preFetch) tryGetCount();
  }

  function clearFilters(requestItems = false): void {
    initiateFilters(true);
    tryGetCount();

    if (requestItems) getWithNewFilters();
  }

  const filtersQuery = computed(() => {
    const otherQuery = Object.keys(route.query).reduce((sum, key) => {
      if (
        !/^(range-.+|attr-.+|discount|best_price|products-sort|shop_ids|brand_id|price)/g.test(
          key
        )
      )
        return sum;
      else {
        if (route.query[key] === undefined) {
          return {
            ...sum,
          };
        }
        return {
          ...sum,
          [key]: route.query[key] || 'true',
        };
      }
    }, {});
    if (Object.keys(otherQuery).length === 0) {
      return '';
    } else {
      const sp = new URLSearchParams(otherQuery);
      return sp.toString();
    }
  });

  return {
    values,
    isChanged,
    atLeastOne,
    initiateFilters,
    getFiltersForRequest,
    filtersChange,
    clearFilters,
    getWithNewFilters,
    cancelActiveFilter,
    formatFiltersForRequest,
    resetValues,
    filtersQuery,
    changeFiltersQuery,
  };
}
